/**
 * /ai/scanner-health — Session 3a PR 18.
 *
 * Health dashboard for the 15 Mutaba'aa scanners running via
 * APScheduler — heartbeats · success rates · auto-pause status.
 * Solvait infrastructure-owned per Session 3a §4.17.
 *
 * AI surfaces (commandment-3 invariant):
 *   1. AiBriefPanel right rail (Mutaba'aa brand)
 *   2. InlineAISuggestion cards (auto-paused investigation · high-cost
 *      scan candidate)
 *   3. TraceableValue wrapping last_run + findings_24h + next_run
 *      (≥3 wrappers)
 */

import { useQuery } from "@tanstack/react-query";
import { Activity, PauseCircle, Play, RefreshCw } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { ErrorPanel, Loading } from "../components/Loading";
import {
  InlineAISuggestion,
  PageHeader,
  TraceableValue,
} from "../components/ui";
import {
  session3aApi,
  type ScannerHealthItem,
} from "../lib/session3a-api";
import { AiBriefPanel } from "../shell/AiBriefPanel";
import { severityChip, showAlertTrace } from "./_session3aHelpers";

type Filter = "all" | "healthy" | "degraded" | "paused";

export function AIScannerHealthPage() {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<Filter>("all");

  const list = useQuery({
    queryKey: ["scanner-health"],
    queryFn: () => session3aApi.listScannerHealth(),
    refetchInterval: 60_000,
  });

  const items: ScannerHealthItem[] = list.data?.items ?? [];
  const kpis = list.data?.kpis ?? { healthy: 0, degraded: 0, paused: 0, total: 0 };

  const filtered = useMemo(() => {
    if (filter === "all") return items;
    return items.filter((r) => r.status === filter);
  }, [items, filter]);

  const pausedScanner = useMemo(
    () => items.find((r) => r.status === "paused"),
    [items],
  );
  const heavyScanner = useMemo(() => {
    return items.reduce<ScannerHealthItem | null>(
      (acc, r) => (!acc || r.findings_24h > acc.findings_24h ? r : acc),
      null,
    );
  }, [items]);

  return (
    <div className="grid grid-cols-[1fr_280px] gap-4">
      <div className="space-y-3">
        <PageHeader
          title={t("ai.scanner_health.title", "AI Scanner Health")}
          subtitle={t(
            "ai.scanner_health.subtitle",
            "15 Mutaba'aa scanners running via APScheduler — heartbeats · success rates · auto-pause status per commandment 64.",
          )}
        />

        {/* KPI cards */}
        <div className="grid grid-cols-4 gap-3">
          <KpiCard
            label={t("ai.scanner_health.kpi.healthy", "Healthy")}
            value={kpis.healthy}
            tone="info"
          />
          <KpiCard
            label={t("ai.scanner_health.kpi.degraded", "Degraded")}
            value={kpis.degraded}
            tone="attention"
          />
          <KpiCard
            label={t("ai.scanner_health.kpi.paused", "Auto-paused")}
            value={kpis.paused}
            tone="critical"
          />
          <KpiCard
            label={t("ai.scanner_health.kpi.total", "Total")}
            value={kpis.total}
            tone="neutral"
          />
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap gap-1.5">
            {(
              [
                ["all", "All"],
                ["healthy", "Healthy"],
                ["degraded", "Degraded"],
                ["paused", "Paused"],
              ] as const
            ).map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => setFilter(key)}
                className={`rounded-full border px-3 py-1 text-xs font-medium ${
                  filter === key
                    ? key === "paused"
                      ? "border-rose-300 bg-rose-50 text-rose-800"
                      : key === "degraded"
                        ? "border-amber-300 bg-amber-50 text-amber-800"
                        : "border-brand-blue bg-brand-blue/10 text-brand-deep"
                    : "border-border-subtle text-text-secondary hover:bg-surface-sunken"
                }`}
              >
                {t(`ai.scanner_health.filter.${key}`, label)}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() =>
              showAlertTrace(
                t(
                  "ai.scanner_health.run_all_note",
                  "Run All Scans Now triggers every registered scanner immediately via POST /api/ai/scanner-health/run-all.",
                ),
              )
            }
            className="inline-flex items-center gap-1.5 rounded-md bg-gradient-to-br from-[#2260C9] to-[#A846BF] px-3 py-1.5 text-sm font-medium text-white hover:opacity-95"
          >
            <RefreshCw className="h-4 w-4" />
            {t("ai.scanner_health.cta.run_all", "Run all scans now")}
          </button>
        </div>

        {pausedScanner && (
          <InlineAISuggestion
            agent="mutabaa"
            icon={<PauseCircle className="h-4 w-4" />}
            severity="critical"
            headline={t(
              "ai.scanner_health.suggestion.paused_headline",
              `${pausedScanner.scan_name} auto-paused after ${pausedScanner.consecutive_failures} consecutive failures.`,
            )}
            body={t(
              "ai.scanner_health.suggestion.paused_body",
              "Likely cause: DB connection drop, schema mismatch, or external integration outage. Investigate then resume.",
            )}
            primaryAction={{
              label: t(
                "ai.scanner_health.suggestion.investigate",
                "Investigate paused scan",
              ),
              onClick: () =>
                showAlertTrace(
                  t(
                    "ai.scanner_health.suggestion.investigate_note",
                    "Investigate drawer surfaces the last 5 failure stack traces + heartbeat history when wiring lands.",
                  ),
                ),
            }}
            suggestionKey={`scanner-paused-${pausedScanner.scan_name}`}
          />
        )}
        {heavyScanner && heavyScanner.findings_24h > 5 && (
          <InlineAISuggestion
            agent="mutabaa"
            icon={<Activity className="h-4 w-4" />}
            severity="info"
            headline={t(
              "ai.scanner_health.suggestion.heavy_headline",
              `${heavyScanner.scan_name} surfaced ${heavyScanner.findings_24h} findings in last 24h.`,
            )}
            body={t(
              "ai.scanner_health.suggestion.heavy_body",
              "High-frequency or high-finding scans are AI-cost candidates. Review whether the cadence can drop without missing violations.",
            )}
            primaryAction={{
              label: t(
                "ai.scanner_health.suggestion.see_findings",
                "See findings",
              ),
              onClick: () =>
                showAlertTrace(
                  t(
                    "ai.scanner_health.suggestion.findings_note",
                    "Findings drawer lists every entity the scan flagged in the window.",
                  ),
                ),
            }}
            suggestionKey={`scanner-heavy-${heavyScanner.scan_name}`}
          />
        )}

        {list.isLoading && <Loading />}
        {list.isError && <ErrorPanel message={(list.error as Error).message} />}

        {filtered.length > 0 && (
          <div className="overflow-hidden rounded-2xl border border-border-subtle bg-white">
            <table className="w-full text-sm">
              <thead className="bg-surface-sunken">
                <tr className="text-left text-[10px] font-semibold uppercase tracking-wide text-text-secondary">
                  <th className="px-3 py-2">{t("ai.scanner_health.col.name", "Scan name")}</th>
                  <th className="px-3 py-2">{t("ai.scanner_health.col.interval", "Interval")}</th>
                  <th className="px-3 py-2">{t("ai.scanner_health.col.last_run", "Last run")}</th>
                  <th className="px-3 py-2">{t("ai.scanner_health.col.status", "Status")}</th>
                  <th className="px-3 py-2">{t("ai.scanner_health.col.findings", "Findings (24h)")}</th>
                  <th className="px-3 py-2">{t("ai.scanner_health.col.failures", "Failures")}</th>
                  <th className="px-3 py-2">{t("ai.scanner_health.col.actions", "Actions")}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((row) => (
                  <tr
                    key={row.scan_name}
                    className="border-t border-border-subtle hover:bg-surface-sunken/40"
                  >
                    <td className="px-3 py-2 font-mono text-xs">
                      <span className="text-[#2260C9]">{row.scan_name}</span>
                    </td>
                    <td className="px-3 py-2 text-xs text-text-secondary">
                      {row.interval_label_en}
                    </td>
                    <td className="px-3 py-2 text-xs text-text-secondary">
                      <TraceableValue
                        onTraceClick={() =>
                          showAlertTrace(
                            t(
                              "ai.scanner_health.trace.last_run",
                              `Last run: ${row.last_run_at ?? "never"}. Next: ${row.next_run_at ?? "TBD"}. Scheduler heartbeat tick every 60s.`,
                            ),
                          )
                        }
                      >
                        <span dir="ltr">
                          {row.last_run_at?.slice(11, 16) ?? "Never"}
                        </span>
                      </TraceableValue>
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${severityChip(
                          row.status === "paused"
                            ? "critical"
                            : row.status === "degraded"
                              ? "attention"
                              : "info",
                        )}`}
                      >
                        {row.status}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <TraceableValue
                        onTraceClick={() =>
                          showAlertTrace(
                            t(
                              "ai.scanner_health.trace.findings",
                              `${row.findings_24h} findings emitted by this scan in last 24h. Each finding becomes a downstream notification or veto event.`,
                            ),
                          )
                        }
                      >
                        <span className="text-text-secondary">{row.findings_24h}</span>
                      </TraceableValue>
                    </td>
                    <td className="px-3 py-2 text-xs text-text-secondary">
                      <TraceableValue
                        onTraceClick={() =>
                          showAlertTrace(
                            t(
                              "ai.scanner_health.trace.failures",
                              `consecutive_failures = ${row.consecutive_failures}. Hits 3 → auto-pause per MutabaaScheduler MAX_CONSECUTIVE_FAILURES.`,
                            ),
                          )
                        }
                      >
                        <span>{row.consecutive_failures}</span>
                      </TraceableValue>
                    </td>
                    <td className="px-3 py-2">
                      {row.status === "paused" ? (
                        <button
                          type="button"
                          onClick={() =>
                            showAlertTrace(
                              t(
                                "ai.scanner_health.resume_note",
                                "Resume re-enables the scan immediately. Wiring: POST /api/ai/scanner-health/{scan_name}/resume.",
                              ),
                            )
                          }
                          className="inline-flex items-center gap-1 rounded border border-emerald-300 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700 hover:bg-emerald-50"
                        >
                          <Play className="h-3 w-3" />
                          {t("ai.scanner_health.action.resume", "Resume")}
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() =>
                            showAlertTrace(
                              t(
                                "ai.scanner_health.run_one_note",
                                `Manual trigger for ${row.scan_name}. Wiring: POST /api/ai/scanner-health/{scan_name}/run.`,
                              ),
                            )
                          }
                          className="inline-flex items-center gap-1 rounded border border-brand-blue px-1.5 py-0.5 text-[10px] font-medium text-[#2260C9] hover:bg-brand-blue/10"
                        >
                          <RefreshCw className="h-3 w-3" />
                          {t("ai.scanner_health.action.run", "Run now")}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!list.isLoading && filtered.length === 0 && (
          <div className="rounded-2xl border border-dashed border-border-subtle p-6 text-center text-sm text-text-tertiary">
            {t(
              "ai.scanner_health.empty",
              "No Mutaba'aa scanners registered — this shouldn't happen. Check scheduler startup.",
            )}
          </div>
        )}
      </div>
      <aside className="rounded-2xl border border-border-subtle bg-white">
        <AiBriefPanel
          agent="mutabaa"
          liveContext={{
            total: kpis.total,
            attention: kpis.degraded,
            critical: kpis.paused,
            top_kind: t("ai.scanner_health.title", "Scanner Health"),
            window_label: "live",
          }}
        />
      </aside>
    </div>
  );
}

function KpiCard({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: "neutral" | "info" | "attention" | "critical";
}) {
  const toneClass = {
    neutral: "border-border-subtle",
    info: "border-brand-blue/40 bg-brand-blue/5",
    attention: "border-amber-300 bg-amber-50",
    critical: "border-rose-300 bg-rose-50",
  }[tone];
  return (
    <div
      className={`relative overflow-hidden rounded-2xl border ${toneClass} bg-white p-3`}
    >
      <div className="absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-[#00D3C2] via-[#2260C9] to-[#A846BF]" />
      <p className="text-xs font-medium text-text-secondary">{label}</p>
      <p className="mt-1 text-2xl font-semibold text-text-primary">{value}</p>
    </div>
  );
}
